import { startCase } from 'lodash';
import moment from 'moment';
import constants, { FILTER_DATA_TYPES } from '../../const/Const';
import { Constants } from '../../const';

// Defined locally (rather than imported from utils/Utils) to avoid a
// dependency cycle: Utils imports the Redux store, which is built from
// commonSlice, which imports INITIAL_DOWNLOADS_FILTERS from this file.
function sortOptions(arr) {
  return arr?.sort((a, b) =>
    a?.username?.toLowerCase().localeCompare(b?.username?.toLowerCase())
  );
}

function formatUserDisplayName(options) {
  if (!options) return 'N/A';
  let fullName = options?.full_name;
  if (!fullName && (options?.user__first_name || options?.user__last_name)) {
    fullName = `${options?.user__first_name || ''} ${
      options?.user__last_name || ''
    }`.trim();
  } else if (!fullName && (options?.first_name || options?.last_name)) {
    fullName = `${options?.first_name || ''} ${
      options?.last_name || ''
    }`.trim();
  }
  fullName = fullName || 'N/A';
  const userName = options?.user__username || options?.username || 'N/A';
  return `${fullName} (${userName})`;
}

export const INITIAL_DOWNLOADS_FILTERS = {
  module: 'name_screen',
  screening_type: 'o',
  dates: {
    start: moment().startOf('month'),
    end: moment(),
  },
};

export function getDownloadsFilters({
  user,
  analystType,
  teamList,
  updatedUsersList,
  country,
  teams,
  managers,
  ratings,
  downloadsFilters,
  industry,
  occupation,
  product,
  transactionType,
  productType,
  channel,
  screen,
  fetchBatchFileOptions,
}) {
  const {
    SEARCH_TYPES,
    SCREEN_TYPES,
    HISTORY_SCREEN_TYPES,
    SEARCH_TYPES_TRANSACTION,
  } = Constants.CommonConst;

  const getManagerIds = () => {
    if (user?.role === 'manager') return [user.id];
    if (Array.isArray(managers)) return managers.map((id) => parseInt(id, 10));
    return [];
  };
  const managerIds = getManagerIds();

  const filters = [
    {
      key: 'module',
      label: 'Module',
      datatype: FILTER_DATA_TYPES.SINGLESELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Name Screening', value: 'name_screen' },
        { label: 'Transaction Screening', value: 'transaction_screen' },
      ],
      isDefault: true,
      isRequired: true,
    },
    {
      key: 'screening_type',
      label: 'Screening Type',
      datatype: FILTER_DATA_TYPES.SINGLESELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Initial Screening', value: SCREEN_TYPES.i.key },
        { label: 'Ongoing Monitoring', value: SCREEN_TYPES.o.key },
      ],
      isDefault: true,
      isRequired: true,
    },
    {
      key: 'batch_id',
      label: 'File Name',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: false,
      options: [],
      fetchOptions: fetchBatchFileOptions,
      showBulkActions: true,
      screeningType:
        HISTORY_SCREEN_TYPES?.[downloadsFilters?.screening_type]?.label ||
        HISTORY_SCREEN_TYPES.i.label,
      module: downloadsFilters?.module || 'name_screen',
      isDefault: true,
    },
    {
      key: 'alerted',
      label: 'Alert/Non-Alert',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Alert', value: 'Alert' },
        { label: 'Non-Alert', value: 'Non-Alert' },
      ],
    },
    {
      key: 'listType',
      label: 'Result Type',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: (() => {
        if (
          downloadsFilters?.alerted?.includes('Non-Alert') &&
          downloadsFilters?.alerted?.length === 1
        ) {
          return [];
        }
        return [
          ...(user?.sanction_permission
            ? [{ label: 'Sanctions', value: 'Sanctions' }]
            : []),
          ...(user?.pep_permission ? [{ label: 'PEP', value: 'PEP' }] : []),
          ...(user?.ams_permission ? [{ label: 'AMS', value: 'AMS' }] : []),
          ...(user?.corporate_permission
            ? [{ label: 'Corporate', value: 'Corporate' }]
            : []),
          ...(user?.legal_permission
            ? [{ label: 'Legal', value: 'Legal' }]
            : []),
          { label: 'MNRL', value: 'MNRL' },
        ];
      })(),
      isDefault: true,
    },
    {
      key: 'status',
      label: 'Status',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Accepted', value: 'Accepted' },
        { label: 'Rejected', value: 'Rejected' },
        { label: 'Pending', value: 'Pending' },
      ],
      isDefault: true,
    },
    {
      key: 'true_match',
      label: 'True Match',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Sanction True Match', value: 'sanction_true_match' },
        { label: 'PEP True Match', value: 'pep_true_match' },
        { label: 'AMS True Match', value: 'ams_true_match' },
      ],
      isDefault: true,
    },
    {
      key: 'dates',
      label: 'Initiated Date',
      datatype: FILTER_DATA_TYPES.DATE,
      hasStaticOptions: true,
      options: [],
      isDefault: true,
      isRequired: true,
      disableFuture: true,
      limitThisMonthToToday: true,
    },
    {
      key: 'due_date',
      label: 'Due Date',
      datatype: FILTER_DATA_TYPES.DATE,
      hasStaticOptions: true,
      options: [],
      isDefault: false,
      disableFuture: false,
    },
    {
      key: 'search_type',
      label: 'Search Type',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        {
          label: SEARCH_TYPES_TRANSACTION?.b?.label,
          value: SEARCH_TYPES_TRANSACTION?.b?.key,
        },
        { label: SEARCH_TYPES?.i?.label, value: SEARCH_TYPES?.i?.key },
        { label: SEARCH_TYPES?.e?.label, value: SEARCH_TYPES?.e?.key },
        { label: SEARCH_TYPES?.v?.label, value: SEARCH_TYPES?.v?.key },
        { label: SEARCH_TYPES?.a?.label, value: SEARCH_TYPES?.a?.key },
      ],
    },
    {
      key: 'country',
      label: 'Country',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: country || [],
      isDefault: false,
    },
    {
      key: 'search_source',
      label: 'Search Source',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Reassigned', value: 'reassigned' },
        { label: 'Assigned', value: 'assigned' },
        { label: 'Referred', value: 'referred' },
        ...(screen === SCREEN_TYPES.o.key
          ? [{ label: 'Rekyc', value: 'rekyc' }]
          : []),
        ...(screen === SCREEN_TYPES.o.key
          ? [{ label: 'IS to OM', value: 'is_to_om' }]
          : []),
        { label: 'Transferred', value: 'transferred' },
        ...(screen === SCREEN_TYPES.o.key
          ? [{ label: 'Daily Monitoring', value: 'daily_monitoring' }]
          : []),
      ],
      isDefault: false,
    },
  ];

  if (
    !(
      downloadsFilters?.search_type?.includes(SEARCH_TYPES?.v?.key) ||
      downloadsFilters?.search_type?.includes(SEARCH_TYPES?.a?.key)
    )
  ) {
    filters.push(
      {
        key: 'risk_type',
        label: 'Risk Rating',
        datatype: FILTER_DATA_TYPES.MULTISELECT,
        hasStaticOptions: true,
        options: ratings?.map((r) => ({ label: startCase(r), value: r })) || [],
        isDefault: false,
      },
      {
        key: 'onboarding_type',
        label: 'Onboarding Type',
        datatype: FILTER_DATA_TYPES.MULTISELECT,
        hasStaticOptions: true,
        options:
          constants?.ONBOARDING_TYPE?.map((item) => ({
            label: item,
            value: item,
          })) || [],
        isDefault: false,
      }
    );
  }

  if (user?.role === 'admin') {
    filters.push({
      key: 'manager_id',
      label: 'Select Manager',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options:
        analystType?.length > 0
          ? (sortOptions(analystType) || [])
              .filter((opt) => opt.analyst_level === -1)
              .map((opt) => ({
                label: formatUserDisplayName(opt),
                value: opt.user_id || opt.id,
              }))
          : [],
      isDefault: false,
    });
  }

  const getManagerUsernames = () => {
    if (user?.role === 'manager') return [user.username];
    if (Array.isArray(managers) && updatedUsersList?.length > 0) {
      return updatedUsersList
        .filter((u) => managers.includes(u.user_id || u.id))
        .map((u) => u.username);
    }
    return [];
  };
  const managerUsernames = getManagerUsernames();

  filters.push(
    {
      key: 'team',
      label: 'Select Team',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: [
        { label: 'Manual', value: 'manual' },
        ...(teamList?.length > 0
          ? (sortOptions(teamList) || [])
              .filter((opt) => {
                if (managerIds.length === 0 && user?.role === 'admin') {
                  return true;
                }
                if (user?.role === 'maker' || user?.role === 'checker') {
                  return opt?.analysts?.some(
                    (analyst) => analyst.username === user?.username
                  );
                }

                if (user?.role === 'maker' || user?.role === 'checker') {
                  return opt?.analysts?.some(
                    (analyst) => analyst.username === user?.username
                  );
                }

                // Check if manager created the team or is a member
                const creatorUsername =
                  opt?.created_by?.username || opt?.created_by;
                const isCreator = managerUsernames.includes(creatorUsername);
                const isMember = opt?.analysts?.some((analyst) =>
                  managerUsernames.includes(analyst.username)
                );

                return isCreator || isMember;
              })
              .map((opt) => ({
                label: `${opt?.name} (${opt?.id})`,
                value: opt?.id,
              }))
          : []),
      ],
      isDefault: false,
    },
    {
      key: 'analyst_id',
      label: 'Select Analyst (Reviewed by)',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options: (() => {
        if (teams && teams.length === 1 && teams[0] === 'manual') {
          return [
            { label: 'No Option Available', value: null, disabled: true },
          ];
        }
        let sourceList = updatedUsersList;

        if (managerIds.length > 0 || managerUsernames.length > 0) {
          // Get analysts from teams where manager is creator OR member
          const managerTeams = (teamList || []).filter((t) => {
            const creator = t?.created_by?.username || t?.created_by;
            const isCreator = managerUsernames.includes(creator);
            const isMember = t?.analysts?.some((a) =>
              managerUsernames.includes(a.username)
            );
            return isCreator || isMember;
          });

          const allAnalysts = managerTeams.flatMap((t) => t.analysts || []);
          const seenIds = new Set();
          sourceList = allAnalysts.filter((analyst) => {
            const id = analyst?.id;
            if (!id || seenIds.has(id)) return false;
            seenIds.add(id);
            return true;
          });
        }

        // Filter by selected teams
        if (teams && teams.length > 0) {
          const selectedTeams = teams.filter((tId) => tId !== 'manual');
          if (selectedTeams.length > 0) {
            const teamAnalysts = (teamList || [])
              .filter((t) => selectedTeams.includes(t.id))
              .flatMap((t) => t.analysts || []);

            const seenIds = new Set();
            sourceList = teamAnalysts.filter((analyst) => {
              const id = analyst?.id;
              if (!id || seenIds.has(id)) return false;
              seenIds.add(id);
              return true;
            });
          }
        }

        const finalOptions = sortOptions(sourceList || []).map((opt) => ({
          label: formatUserDisplayName(opt),
          value: opt?.user_id || opt?.id,
        }));

        return finalOptions.length > 0
          ? finalOptions
          : [{ label: 'No analysts available', value: null, disabled: true }];
      })(),
      isDefault: false,
    },
    {
      key: 'created_by_id',
      label: 'Created By',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options:
        updatedUsersList?.length > 0
          ? (
              sortOptions(
                updatedUsersList?.filter(
                  (_user, index, self) =>
                    index === self?.findIndex((u) => u?.id === _user?.id)
                ) || []
              ) || []
            ).map((opt) => ({
              label: formatUserDisplayName(opt),
              value: opt?.user_id || opt?.id,
            }))
          : [],
      isDefault: false,
    }
  );

  if (downloadsFilters?.status?.includes('Pending')) {
    filters.push({
      key: 'pending_with',
      label: 'Pending With',
      datatype: FILTER_DATA_TYPES.MULTISELECT,
      hasStaticOptions: true,
      options:
        updatedUsersList?.length > 0
          ? (
              sortOptions(
                updatedUsersList?.filter(
                  (_user, index, self) =>
                    index === self?.findIndex((u) => u?.id === _user?.id)
                ) || []
              ) || []
            ).map((opt) => ({
              label: formatUserDisplayName(opt),
              value: opt?.user_id || opt?.id,
            }))
          : [],
      isDefault: false,
    });
  }

  const filterConfigs = [
    { key: 'channel', label: 'Channel Type', values: channel?.values },
    { key: 'occupation', label: 'Occupation', values: occupation },
    { key: 'product', label: 'Product', values: product },
    { key: 'industry', label: 'Industry', values: industry },
    {
      key: 'transaction_type',
      label: 'Transaction Type',
      values: transactionType?.values,
    },
    {
      key: 'transaction_product_type',
      label: 'Transaction Product Type',
      values: productType?.values,
    },
  ];

  filterConfigs.forEach(({ key, label, values }) => {
    if (values?.length > 0) {
      filters.push({
        key,
        label,
        datatype: FILTER_DATA_TYPES.MULTISELECT,
        hasStaticOptions: true,
        options: values.map((item) => ({
          label: startCase(item),
          value: item,
        })),
        isDefault: false,
      });
    }
  });

  return filters;
}
